/** Google Maps directions for one specific property.
 *
 *  Everything here is a pure function of the property record passed in — there is no module state,
 *  no cache and no default address, so a stale React value or a rapid property switch cannot make
 *  the button point at the wrong place: the URL is derived at the moment of the click from the
 *  record being rendered.
 */

const MAPS_BASE = "https://www.google.com/maps/dir/?api=1";
const MAX_DESTINATION = 300;

/** Strip control characters, collapse runs of whitespace, trim, and bound the length. */
function clean(value) {
  if (typeof value !== "string") return "";
  return value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_DESTINATION);
}

/** A street line worth navigating to has a number and an actual word in it. */
export function hasUsableAddress(property) {
  const street = clean(property?.address);
  return Boolean(property?._id) && street.length >= 5 && /\d/.test(street)
    && /[a-z]{3,}/i.test(street);
}

/** The exact destination for THIS property: street line, then city, then state. */
export function destinationFor(property) {
  if (!hasUsableAddress(property)) return null;
  const parts = [clean(property.address), clean(property.city), clean(property.state)]
    .filter(Boolean);
  return clean(parts.join(", "));
}

/** encodeURIComponent leaves ! ' ( ) * alone; encode them too so no address can shape the URL. */
function encodeStrict(value) {
  return encodeURIComponent(value).replace(/[!'()*]/g, (c) =>
    `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

/** The Google Maps URL for this property, or null when the address is not usable.
 *  Only the destination is sent — never the property id or any other internal data. */
export function directionsUrl(property) {
  const destination = destinationFor(property);
  if (!destination) return null;
  return `${MAPS_BASE}&destination=${encodeStrict(destination)}&travelmode=driving`;
}

export const DIRECTIONS_UNAVAILABLE =
  "Directions unavailable — this property does not have a valid address.";
